import * as chroma from "chroma-js";
import { ColorPalette, OrganizationBranding } from "./branding.types";

const STYLE_ID = "org-branding";
const FAVICON_ID = "org-favicon";
const DEFAULT_DOCUMENT_TITLE = "GeoActivate";

function removeAllFaviconLinks(): void {
    document.querySelectorAll('link[rel="icon"]').forEach((el) => el.remove());
}

function restoreDefaultFavicons(): void {
    const link16 = document.createElement("link");
    link16.rel = "icon";
    link16.type = "image/png";
    link16.href = "favicon-16x16.png";
    document.head.appendChild(link16);
    const link32 = document.createElement("link");
    link32.rel = "icon";
    link32.type = "image/png";
    link32.href = "favicon-32x32.png";
    document.head.appendChild(link32);
}

/** Default primary/accent when no branding is set. Centralized for consistency. */
export const DEFAULT_BRANDING_PRIMARY = "#F10051";
export const DEFAULT_BRANDING_ACCENT = "#004CC4";

type Palette = Record<number, string>;

const HUES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

function toPalette(p: ColorPalette | undefined): Palette {
    if (!p || typeof p !== "object") return {} as Palette;
    const out: Palette = {} as Palette;
    HUES.forEach((h) => {
        if (p[h]) out[h] = p[h]!;
    });
    return out;
}

function generatePalette(hex: string): Palette {
    if (!chroma.valid(hex)) return {} as Palette;
    const base = chroma(hex);
    const light = chroma.scale(["white", base]).mode("lrgb").colors(50)[1];
    const dark = chroma.scale(["black", base]).mode("lrgb").colors(10)[1];
    const scale = chroma.scale([light, base, dark]).domain([0.05, 0.5, 0.95]).mode("lrgb");
    const hues = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
    const domain = [0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9];
    const out: Palette = {} as Palette;
    hues.forEach((h, i) => {
        out[h] = scale(domain[i]).hex();
    });
    return out;
}

function contrastColor(hex: string): string {
    if (!chroma.valid(hex)) return "#ffffff";
    const c = chroma(hex);
    return chroma.contrast(c, "#000") > chroma.contrast(c, "#fff") ? "#000000" : "#ffffff";
}

function rgbString(hex: string): string {
    if (!chroma.valid(hex)) return "255,255,255";
    const [r, g, b] = chroma(hex).rgb();
    return `${Math.round(r)},${Math.round(g)},${Math.round(b)}`;
}

function rgbaString(hex: string, alpha: number): string {
    if (!chroma.valid(hex)) return `rgba(255,255,255,${alpha})`;
    const [r, g, b] = chroma(hex).rgb();
    return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${alpha})`;
}

export interface BuildBrandingCssVarsOptions {
    primaryHex: string;
    accentHex: string;
    primaryPalette?: ColorPalette | null;
    accentPalette?: ColorPalette | null;
    /** Custom tertiary color/palette. When omitted, tertiary is derived from
     * accentHex via a +30° hue shift (legacy behavior). */
    tertiaryHex?: string | null;
    tertiaryPalette?: ColorPalette | null;
    primaryBaseShade?: number;
    accentBaseShade?: number;
    tertiaryBaseShade?: number;
}

/** Build CSS custom properties for Fuse + ac-* from primary and accent. */
export function buildBrandingCssVars(options: BuildBrandingCssVarsOptions): Record<string, string> {
    const {
        primaryHex,
        accentHex,
        primaryPalette: customPrimary,
        accentPalette: customAccent,
        tertiaryHex: customTertiaryHex,
        tertiaryPalette: customTertiary,
        primaryBaseShade = 500,
        accentBaseShade = 500,
        tertiaryBaseShade = 500,
    } = options;
    const primaryPalette = customPrimary ? toPalette(customPrimary) : generatePalette(primaryHex);
    const accentPalette = customAccent ? toPalette(customAccent) : generatePalette(accentHex);
    const primaryBase = primaryPalette[primaryBaseShade as keyof Palette] || primaryPalette[500] || primaryHex;
    const accentBase = accentPalette[accentBaseShade as keyof Palette] || accentPalette[500] || accentHex;
    const onPrimary = contrastColor(primaryBase);
    const onAccent = contrastColor(accentBase);
    const tertiaryHex =
        customTertiaryHex && chroma.valid(customTertiaryHex)
            ? customTertiaryHex
            : chroma.valid(accentHex)
              ? chroma(accentHex).set("hsl.h", "+30").hex()
              : "#2589A3";
    const tertiaryPalette = customTertiary ? toPalette(customTertiary) : generatePalette(tertiaryHex);

    const vars: Record<string, string> = {};

    // Fuse primary (hex + -rgb for Tailwind)
    if (primaryBase) {
        vars["--fuse-primary"] = primaryBase;
        vars["--fuse-primary-rgb"] = rgbString(primaryBase);
        vars["--fuse-on-primary"] = onPrimary;
        vars["--fuse-on-primary-rgb"] = rgbString(onPrimary);
        HUES.forEach((h) => {
            if (primaryPalette[h]) {
                vars[`--fuse-primary-${h}`] = primaryPalette[h];
                vars[`--fuse-primary-${h}-rgb`] = rgbString(primaryPalette[h]);
            }
        });
    }

    // Fuse accent (hex + -rgb for Tailwind)
    if (accentBase) {
        vars["--fuse-accent"] = accentBase;
        vars["--fuse-accent-rgb"] = rgbString(accentBase);
        vars["--fuse-on-accent"] = onAccent;
        vars["--fuse-on-accent-rgb"] = rgbString(onAccent);
        HUES.forEach((h) => {
            if (accentPalette[h]) {
                vars[`--fuse-accent-${h}`] = accentPalette[h];
                vars[`--fuse-accent-${h}-rgb`] = rgbString(accentPalette[h]);
            }
        });
    }

    // ac-* primary (50-900)
    HUES.forEach((h) => {
        if (primaryPalette[h]) vars[`--ac-color-primary-${h}`] = primaryPalette[h];
    });
    if (primaryBase) vars["--ac-color-primary"] = primaryBase;

    // ac-* secondary (50-900)
    HUES.forEach((h) => {
        if (accentPalette[h]) vars[`--ac-color-secondary-${h}`] = accentPalette[h];
    });
    if (accentBase) vars["--ac-color-secondary"] = accentBase;

    // ac-* tertiary (derived from accent hue shift)
    [100, 200, 300, 400, 500, 600, 700, 800, 900].forEach((h) => {
        if (tertiaryPalette[h]) vars[`--ac-color-tertiary-${h}`] = tertiaryPalette[h];
    });
    const tertiaryBase = tertiaryPalette[tertiaryBaseShade as keyof Palette] || tertiaryPalette[500];
    if (tertiaryBase) vars["--ac-color-tertiary"] = tertiaryBase;

    // Angular Material select panel
    if (primaryBase) {
        vars["--mat-option-selected-state-layer-color"] = rgbaString(primaryBase, 0.08);
        vars["--mdc-option-selected-state-label-text-color"] = primaryBase;
    }

    return vars;
}

/** Inject branding styles into the document and update favicon. */
export function applyBrandingStyles(branding: OrganizationBranding | null): void {
    removeBrandingStyles();

    if (!branding) {
        return;
    }

    const {
        primaryColor,
        accentColor,
        primaryPalette,
        accentPalette,
        tertiaryColor,
        tertiaryPalette,
        primaryBaseShade,
        accentBaseShade,
        tertiaryBaseShade,
        faviconUrl,
        documentTitle,
        fontFamily,
    } = branding;

    if (primaryColor || accentColor || primaryPalette || accentPalette) {
        const primary = primaryColor || DEFAULT_BRANDING_PRIMARY;
        const accent = accentColor || DEFAULT_BRANDING_ACCENT;
        const vars = buildBrandingCssVars({
            primaryHex: primary,
            accentHex: accent,
            primaryPalette: primaryPalette ?? undefined,
            accentPalette: accentPalette ?? undefined,
            tertiaryHex: tertiaryColor ?? undefined,
            tertiaryPalette: tertiaryPalette ?? undefined,
            primaryBaseShade: primaryBaseShade ?? 500,
            accentBaseShade: accentBaseShade ?? 500,
            tertiaryBaseShade: tertiaryBaseShade ?? 500,
        });
        if (fontFamily) vars["--fuse-font-sans"] = fontFamily;
        const decls = Object.entries(vars)
            .map(([k, v]) => `${k}:${v}`)
            .join(";");
        const style = document.createElement("style");
        style.id = STYLE_ID;
        const rootSelectors =
            ":root, body, body.theme-default, body.theme-brand, .theme-default, .theme-brand, .cdk-overlay-container, .cdk-overlay-pane";
        style.textContent =
            `${rootSelectors} { ${decls} }\n` +
            (fontFamily
                ? `html, body, body *:not(.material-icons):not(.mat-icon):not([class*="mat-icon"]):not([class*="heroicon"]):not(mat-icon):not(mat-icon *), .mat-typography, .mdc-typography, .mat-app-background, .cdk-overlay-container, .cdk-overlay-container *:not(.material-icons):not(.mat-icon):not([class*="mat-icon"]):not([class*="heroicon"]):not(mat-icon):not(mat-icon *) { font-family: ${fontFamily} !important; }\n` +
                  `.material-icons, .mat-icon, mat-icon, [class*="mat-icon"], [class*="heroicon"] { font-family: 'Material Icons' !important; }\n`
                : "") +
            `body .mat-mdc-form-field .mat-mdc-select-arrow,\n` +
            `body.theme-default .mat-mdc-form-field .mat-mdc-select-arrow,\n` +
            `body.theme-brand .mat-mdc-form-field .mat-mdc-select-arrow { border-top-color: var(--fuse-primary); color: var(--fuse-primary); }\n` +
            `.cdk-overlay-container .mat-mdc-option.mdc-list-item--selected .mdc-list-item__primary-text { color: var(--fuse-primary); }\n` +
            `.cdk-overlay-container .mat-mdc-option.mdc-list-item--selected .mat-pseudo-checkbox-checked { color: var(--fuse-primary); }\n` +
            `.cdk-overlay-container .mat-mdc-option.mdc-list-item--selected .mat-pseudo-checkbox-checked::after { border-color: var(--fuse-primary); }\n` +
            `.cdk-overlay-container .mat-mdc-option.mdc-list-item--selected .mat-icon { color: var(--fuse-primary); }\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__track,\n` +
            `body.theme-default .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__track,\n` +
            `body.theme-brand .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__track { background-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__track::before,\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__track::after { background-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__handle::before,\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__handle::after { background-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-slide-toggle .mdc-switch.mdc-switch--selected .mdc-switch__icon { color: var(--fuse-on-primary); }\n` +
            `body fuse-loading-bar .mat-mdc-progress-bar .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner { background-color: var(--fuse-primary); }\n` +
            `body fuse-loading-bar .mat-mdc-progress-bar .mdc-linear-progress__bar-inner { background-color: var(--fuse-primary); border-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-progress-bar.mat-primary .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner { border-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-progress-bar.mat-accent .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner { border-color: var(--fuse-accent); }\n` +
            `.theme-brand .light .mat-mdc-progress-bar .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner,\n.theme-brand.light .mat-mdc-progress-bar .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner { border-color: var(--fuse-primary); }\n` +
            `.theme-brand .light .mat-mdc-progress-bar.mat-accent .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner,\n.theme-brand.light .mat-mdc-progress-bar.mat-accent .mdc-linear-progress__primary-bar .mdc-linear-progress__bar-inner { border-color: var(--fuse-accent); }\n` +
            `body .mat-mdc-progress-bar.mat-primary { --mdc-linear-progress-active-indicator-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-progress-bar.mat-accent { --mdc-linear-progress-active-indicator-color: var(--fuse-accent); }\n` +
            `.theme-brand .light .mat-mdc-progress-bar,\n.theme-brand.light .mat-mdc-progress-bar { --mdc-linear-progress-active-indicator-color: var(--fuse-primary); }\n` +
            `.theme-brand .light .mat-mdc-progress-bar.mat-accent,\n.theme-brand.light .mat-mdc-progress-bar.mat-accent { --mdc-linear-progress-active-indicator-color: var(--fuse-accent); }\n` +
            `body .mat-mdc-tab-group .mat-mdc-tab.mdc-tab--active .mdc-tab-indicator__content--underline { border-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-tab-nav-bar .mat-mdc-tab.mdc-tab--active .mdc-tab-indicator__content--underline { border-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-tab-group .mat-mdc-tab:hover .mdc-tab__text-label { color: var(--fuse-primary); }\n` +
            `body .mat-mdc-tab-group .mat-mdc-tab.mdc-tab--active:hover .mdc-tab-indicator__content--underline { border-color: var(--fuse-primary); }\n` +
            `body .mat-mdc-tab-nav-bar .mat-mdc-tab:hover .mdc-tab__text-label { color: var(--fuse-primary); }\n` +
            `body .mat-mdc-tab-nav-bar .mat-mdc-tab.mdc-tab--active:hover .mdc-tab-indicator__content--underline { border-color: var(--fuse-primary); }\n`;
        document.head.appendChild(style);
    }

    if (fontFamily && !(primaryColor || accentColor)) {
        const style = document.createElement("style");
        style.id = STYLE_ID;
        style.textContent = `:root, body { --fuse-font-sans: ${fontFamily}; }`;
        document.head.appendChild(style);
    }

    if (documentTitle) {
        document.title = documentTitle;
    }

    if (faviconUrl) {
        removeAllFaviconLinks();
        const href = faviconUrl.startsWith("http") || faviconUrl.startsWith("/") ? faviconUrl : `/${faviconUrl}`;
        const link = document.createElement("link");
        link.id = FAVICON_ID;
        link.rel = "icon";
        link.type = "image/png";
        link.href = `${href}?v=1`;
        document.head.appendChild(link);
    }
}

/** Remove injected branding style, org favicon and restore default document title. */
export function removeBrandingStyles(): void {
    document.getElementById(STYLE_ID)?.remove();
    document.getElementById(FAVICON_ID)?.remove();
    removeAllFaviconLinks();
    restoreDefaultFavicons();
    document.title = DEFAULT_DOCUMENT_TITLE;
}
